import { useState, forwardRef, useImperativeHandle, useRef } from 'react'
import Api from '../../component/Api.js'
import Modal from './components/common/modal.js'
import { toast } from 'react-toastify';
const { http } = Api()

const ISChangePassword = forwardRef((props, ref) => {
    const modalRef = useRef()
    const [isPassword, setIsPassword] = useState('')
    const [isConfirmPassword, setIsConfirmPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [msgToast, setMsgToast] = useState(null)
    const [passwordErrors, setPasswordErrors] = useState([])
    const [showPasswordInfo, setShowPasswordInfo] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [passwordMismatch, setPasswordMismatch] = useState(false)

    const showToast = (message, type = 'info') => {
        setMsgToast({ message, type })
        setTimeout(() => setMsgToast(null), 3000)
    }

    const validatePassword = (password) => {
        if (password.length < 8) return ['Password must be at least 8 characters long']
        if (!/^[A-Z]/.test(password)) return ['Password must start with an uppercase letter']
        if (!/[a-z]/.test(password)) return ['Password must contain at least one lowercase letter']
        if (!/\d/.test(password)) return ['Password must contain at least one digit']
        if (!/[\W_]/.test(password)) return ['Password must contain at least one special character']
        return []
    }

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value
        setIsPassword(newPassword)
        if (newPassword && showPasswordInfo) setShowPasswordInfo(false)
        setPasswordErrors(newPassword ? validatePassword(newPassword) : [])
        setPasswordMismatch(isConfirmPassword ? newPassword !== isConfirmPassword : false)
    }

    const handleConfirmPasswordChange = (e) => {
        const confirmPassword = e.target.value
        setIsConfirmPassword(confirmPassword)
        setPasswordMismatch(confirmPassword ? isPassword !== confirmPassword : false)
    }

    const resetFields = () => {
        setIsPassword('')
        setIsConfirmPassword('')
        setPasswordErrors([])
        setPasswordMismatch(false)
        setShowPasswordInfo(false)
        setShowPassword(false)
        setShowConfirmPassword(false)
        setMsgToast(null)
    }

    useImperativeHandle(ref, () => ({
        open(id, uid, type) {
            resetFields()
            modalRef.current?.open()
        }
    }))

    const close = () => {
        modalRef.current?.close()
        resetFields()
    }

    const submit = async () => {
        if (!isPassword || !isConfirmPassword) {
            showToast('All fields required', 'warning')
            return
        }

        const errors = validatePassword(isPassword)
        if (errors.length > 0) {
            showToast(errors[0], 'danger')
            return
        }

        if (isPassword !== isConfirmPassword) {
            setPasswordMismatch(true)
            showToast('Passwords do not match', 'danger')
            return
        }

        setIsLoading(true)

        try {
            const response = await http.post('/is/changeIsPassword', {
                isPassword,
                confirm_password: isConfirmPassword
            })

            if (response.data.error || response.data.status === 'error') {
                showToast(response.data.message || response.data.error || 'Password update failed', 'danger')
            } else {
                toast.success('Password updated successfully')
                resetFields()
                setTimeout(() => close(), 1500)
            }
        } catch (error) {
            showToast('Password update failed', 'danger')
        } finally {
            setIsLoading(false)
        }
    }

    const EyeIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="gray" viewBox="0 0 16 16">
            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
        </svg>
    )

    const EyeSlashIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="gray" viewBox="0 0 16 16">
            <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
            <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
            <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
        </svg>
    )

    return (
        <Modal
            ref={modalRef}
            title="Change Password"
            showInfoButton={true}
            onInfoClick={() => setShowPasswordInfo(!showPasswordInfo)}
            footer={
                <div className="d-flex justify-content-center w-100">
                    <button
                        type="button"
                        className="btn btn-primary mx-2"
                        disabled={isLoading || passwordErrors.length > 0 || passwordMismatch}
                        onClick={submit}
                    >
                        {isLoading ? 'Updating...' : 'Update'}
                    </button>
                    <button type="button" className="btn btn-light" onClick={close}>
                        Cancel
                    </button>
                </div>
            }
        >
            {msgToast && (
                <div className={`alert alert-${msgToast.type} alert-dismissible fade show`} role="alert">
                    {msgToast.message}
                    <button type="button" className="btn-close" onClick={() => setMsgToast(null)}></button>
                </div>
            )}

            <form autoComplete="off" className="mt-3">
                <div className="mb-2">
                    <div className="input-group">
                        <input
                            type={showPassword ? "text" : "password"}
                            className={`form-control ${passwordErrors.length > 0 ? 'is-invalid' : ''}`}
                            placeholder="Enter new password"
                            autoComplete="new-password"
                            value={isPassword}
                            onChange={handlePasswordChange}
                        />
                        <button
                            type="button"
                            className="btn border"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        >
                            {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                    </div>

                    {passwordErrors.length > 0 && (
                        <div className="invalid-feedback d-block">
                            <ul className="mb-0 ps-3">
                                {passwordErrors.map((error, index) => (
                                    <li key={index} className="small">{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {showPasswordInfo && (
                        <div className="card mt-2 border-info" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="card-body p-2">
                                <p className="small text-muted mb-1"><strong>Password Requirements:</strong></p>
                                <ul className="small text-muted mb-0 ps-3" style={{ fontSize: '12px' }}>
                                    <li>8-12 characters long</li>
                                    <li>Must start with an uppercase letter</li>
                                    <li>At least one lowercase letter</li>
                                    <li>At least one digit (0-9)</li>
                                    <li>At least one special character (!@#$%^&*)</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mb-2">
                    <div className="input-group">
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            className={`form-control ${passwordMismatch ? 'is-invalid' : ''}`}
                            placeholder="Confirm Password"
                            autoComplete="new-password"
                            value={isConfirmPassword}
                            onChange={handleConfirmPasswordChange}
                        />
                        <button
                            type="button"
                            className="btn border"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        >
                            {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                    </div>

                    {passwordMismatch && (
                        <div className="invalid-feedback d-block">
                            <span className="small">Passwords do not match</span>
                        </div>
                    )}
                    <span className='text-secondary'><small>* Use this password exclusively to log in to this system.</small></span>
                </div>
            </form>
        </Modal>
    )
})

export default ISChangePassword